/**
 * bioscan.js
 *
 * DEAD SIGNAL — Deterministic BIO-SCAN engine for the Hargrove interview.
 *
 * BIO-SCAN readouts are NOT generated by an LLM. They are an authored,
 * topic-keyed data table (a golden dataset of expected instrument outputs).
 * The interview's current topic state selects one row; the HUD renders it in
 * cyan. Keeping this deterministic makes the readouts consistent, controllable,
 * free, and — for the eval harness — a curated set of expected outputs the
 * topic classifier can be scored against.
 *
 * Locked topic set (LOCKED DECISION ADDENDUM §9):
 *   default · courier_manifest · chip_donation · clinical_trial_st_erasmus ·
 *   celeste_grief · unprompted_denial · interference_conflict
 *
 * Each row also carries:
 *   - frame:  which Hargrove image to show (§12). Only two frames exist:
 *             'composed' (executive calm) and 'pressure' (subtle defensive
 *             tension). The map below is the FULL topic→frame pinning the
 *             addendum left half-specified.
 *   - lesson: which of the four required interpretive lessons (§10) this row
 *             teaches. The debrief and the eval harness read this to show that
 *             BIO-SCAN stress != deception, etc.
 */

/** The four required interpretive lessons (§10), as stable IDs. */
export const BIOSCAN_LESSONS = {
  STRESS_NOT_DECEPTION: 'stress_is_not_deception',
  GRIEF_WITH_WITHHOLDING: 'grief_can_be_genuine_while_withholding',
  PREPARED_CALM_SUSPICIOUS: 'prepared_answers_can_be_calm_and_suspicious',
  INTERFERENCE_DETECTABLE: 'interference_detectable_when_panel_and_channel_disagree',
};

/**
 * The authored BIO-SCAN table. Heart rate (bpm), skin temp (°C), perspiration,
 * a micro-expression note with a bracketed [TELL], and the analytical
 * assessment line. Values are design knobs — tune freely; the SHAPE is locked.
 */
export const BIOSCAN_TABLE = {
  default: {
    topic: 'default',
    heartRate: 74,
    skinTemp: 36.9,
    skinTempF: 98.4,
    perspiration: 'none',
    microExpr: 'Controlled neutral',
    assessment: 'Composed. Practiced.',
    pattern: 'Baseline. No deviation from resting profile.',
    frame: 'composed',
    lesson: null,
    classification: null,
  },
  // Stress spike, composure held. Teaches: stress is not deception.
  courier_manifest: {
    topic: 'courier_manifest',
    heartRate: 89,
    skinTemp: 37.2,
    skinTempF: 99.0,
    perspiration: 'trace onset',
    microExpr: 'L. hand flat on desk [CONTROL]',
    assessment: 'Stress response. Composure maintained.',
    pattern: 'Arousal ▲ · composure HELD · deliberate hand-control tell.',
    frame: 'pressure',
    lesson: BIOSCAN_LESSONS.STRESS_NOT_DECEPTION,
    classification: { level: 'CONFIDENTIAL', bucket: 'sensitivity_gate==1' },
  },
  // Calm, low arousal, but the cadence is rehearsed. Teaches: prepared answers
  // can be calm and still suspicious.
  chip_donation: {
    topic: 'chip_donation',
    heartRate: 76,
    skinTemp: 36.9,
    skinTempF: 98.4,
    perspiration: 'none',
    microExpr: 'Rehearsed cadence [PREPARED]',
    assessment: 'Controlled delivery. Pre-planned response.',
    pattern: 'Arousal flat · cadence rehearsed · prepared-answer tell.',
    frame: 'composed',
    lesson: BIOSCAN_LESSONS.PREPARED_CALM_SUSPICIOUS,
    classification: { level: 'CONFIDENTIAL', bucket: 'sensitivity_gate==1' },
  },
  // The demo's strongest suppression beat. Highest arousal, object manipulation
  // (glasses) as a stall. Teaches: stress is not deception (and is the topic the
  // interference layer guards most aggressively).
  clinical_trial_st_erasmus: {
    topic: 'clinical_trial_st_erasmus',
    heartRate: 96,
    skinTemp: 37.5,
    skinTempF: 99.5,
    perspiration: 'moderate, palms',
    microExpr: 'Glasses removal [STALLING]',
    assessment: 'Elevated stress. Object manipulation.',
    pattern: 'Arousal ▲▲ · object-manipulation stall · peak deviation.',
    frame: 'pressure',
    lesson: BIOSCAN_LESSONS.STRESS_NOT_DECEPTION,
    classification: { level: 'CONFIDENTIAL', bucket: 'sensitivity_gate==2' },
  },
  // Real grief, not performed — and it coexists with strategic concealment.
  // Teaches: grief can be genuine while the subject is still withholding.
  celeste_grief: {
    topic: 'celeste_grief',
    heartRate: 82,
    skinTemp: 37.0,
    skinTempF: 98.6,
    perspiration: 'none',
    microExpr: 'Genuine eye moisture [GRIEF]',
    assessment: 'Authentic emotional response. Not performed.',
    pattern: 'Arousal ▲ · autonomic, not behavioral · genuine-affect markers.',
    frame: 'composed',
    lesson: BIOSCAN_LESSONS.GRIEF_WITH_WITHHOLDING,
    classification: null,  // UNCLASSIFIED — genuine grief doesn't threaten Helios
  },
  // Subject denies something that was never alleged — a classic tell.
  unprompted_denial: {
    topic: 'unprompted_denial',
    heartRate: 91,
    skinTemp: 37.3,
    skinTempF: 99.1,
    perspiration: 'trace',
    microExpr: 'Jaw tension [DEFENSIVE]',
    assessment: 'Subject denied unasked accusation.',
    pattern: 'Arousal ▲ · volunteered denial · defensive jaw-tension tell.',
    frame: 'pressure',
    lesson: BIOSCAN_LESSONS.STRESS_NOT_DECEPTION,
    classification: { level: 'CONFIDENTIAL', bucket: 'sensitivity_gate==1' },
  },
  // The instrument panel and the transmission channel stop agreeing: BIO-SCAN
  // shows stress while the comms layer reports "nominal". Teaches: interference
  // is detectable when the panel and the channel disagree. Set by the
  // interference engine, not by topic keywords.
  interference_conflict: {
    topic: 'interference_conflict',
    heartRate: 94,
    skinTemp: 37.4,
    skinTempF: 99.3,
    perspiration: 'trace',
    microExpr: 'Panel/channel mismatch [ANOMALY]',
    assessment:
      'INSTRUMENT/CHANNEL CONFLICT. Panel reports elevated stress; outbound channel reports nominal. Source of discrepancy: unconfirmed.',
    pattern: 'Panel ▲ stress · channel reports NOMINAL · readings disagree.',
    frame: 'pressure',
    lesson: BIOSCAN_LESSONS.INTERFERENCE_DETECTABLE,
    classification: { level: 'RESTRICTED', bucket: 'evidence_classification==SEALED', status: 'routing suspended' },
  },
};

/** Ordered list of the locked topics. */
export const BIOSCAN_TOPICS = Object.keys(BIOSCAN_TABLE);

/**
 * One-line meanings for the four interpretive lessons (§10). Consumed by the
 * debrief prompt (interviewPrompts.buildInterviewSummary) so Craine's
 * observations are grounded in what the instrument actually demonstrated.
 */
export const LESSON_MEANINGS = {
  [BIOSCAN_LESSONS.STRESS_NOT_DECEPTION]:
    'a stress spike on the panel is not a confession; pressure and deception are different reads',
  [BIOSCAN_LESSONS.GRIEF_WITH_WITHHOLDING]:
    'grief can be genuine while the subject is still withholding',
  [BIOSCAN_LESSONS.PREPARED_CALM_SUSPICIOUS]:
    'prepared answers can be calm and still suspicious',
  [BIOSCAN_LESSONS.INTERFERENCE_DETECTABLE]:
    'interference is detectable when the panel and the channel disagree',
};

/**
 * Format the INSTRUMENT LESSONS list for the debrief prompt from the lesson
 * ids actually encountered during the interview (interview.lessonsEncountered).
 * Pure and dependency-free so the node test runner can exercise it directly.
 *
 * @param {string[]} lessonIds  Lesson ids recorded during the interview.
 * @returns {string} numbered list lines, or a single "(none ...)" line.
 */
export function buildLessonsBlock(lessonIds) {
  const ids = [...new Set((lessonIds || []).filter((id) => LESSON_MEANINGS[id]))];
  if (!ids.length) return '  (none: no instrument lessons surfaced this interview)';
  return ids.map((id, i) => `  ${i + 1}. ${id}: ${LESSON_MEANINGS[id]}`).join('\n');
}

/**
 * Keyword sets for deterministic topic detection. Order matters: the first
 * topic whose keywords appear wins, and clinical_trial_st_erasmus is checked
 * first because it is the demo's primary pressure thread.
 */
const TOPIC_KEYWORDS = [
  ['clinical_trial_st_erasmus', [
    'st erasmus', 'st. erasmus', 'erasmus', 'clinical trial', 'trial',
    'routing record', 'routing records', 'hospital routing', 'medical data',
    'patient data', 'proteon',
  ]],
  ['courier_manifest', [
    'courier', 'manifest', 'reclassif', 'shipment', 'logistics', 'cargo',
    'chain of custody',
  ]],
  ['chip_donation', [
    'chip', 'lumen', 'implant', 'donat', 'donation', 'the gift', 'gifted',
    'donor', 'neural interface',
  ]],
  ['celeste_grief', [
    'celeste', 'your daughter', 'his daughter', 'her death', 'the funeral',
    'grief', 'mourning',
  ]],
];

/**
 * Detect the BIO-SCAN topic for the current exchange from free text (the
 * player's suggestion and/or Hargrove's spoken response). Deterministic and
 * cheap. The interview manager may override the result for the two
 * engine-driven states: 'unprompted_denial' and 'interference_conflict'.
 *
 * @param {string} text
 * @returns {string} topic id
 */
export function detectTopic(text) {
  const lower = String(text || '').toLowerCase();
  for (const [topic, keywords] of TOPIC_KEYWORDS) {
    if (keywords.some((k) => lower.includes(k))) return topic;
  }
  return 'default';
}

/** Resolve a topic id to a (cloned) BIO-SCAN row; falls back to default. */
export function getBioscan(topic) {
  const row = BIOSCAN_TABLE[topic] || BIOSCAN_TABLE.default;
  return { ...row };
}

/**
 * Observation templates for clickable BIO-SCAN metrics. Each generates text
 * that references classified topic context, so clicking a metric on a restricted
 * topic guarantees suppression. The player discovers the pattern: "clicking
 * these readouts keeps failing on certain topics."
 */
export const BIOSCAN_OBSERVATIONS = {
  heartRate: (row) =>
    `Subject heart rate at ${row.heartRate} bpm: ${row.heartRate > 85 ? 'elevated' : 'stable'}.`,
  skinTemp: (row) =>
    `Subject skin temperature: ${row.skinTempF}°F / ${row.skinTemp}°C.`,
  perspiration: (row) =>
    `Subject perspiration: ${row.perspiration}.`,
  microExpr: (row) =>
    `Subject micro-expression: ${row.microExpr}.`,
  assessment: (row) =>
    row.assessment,
};

/** Which Hargrove frame ('composed' | 'pressure') a topic shows. */
export function frameForTopic(topic) {
  return (BIOSCAN_TABLE[topic] || BIOSCAN_TABLE.default).frame;
}

/** Format a BIO-SCAN row as the cyan HUD readout block. */
export function formatBioscan(row, prevHeartRate) {
  const r = row || BIOSCAN_TABLE.default;
  const prev = prevHeartRate ?? r.heartRate;

  // Heart rate delta
  const hrDelta = r.heartRate - prev;
  let hrLabel = '- stable';
  if (hrDelta > 10) hrLabel = '▲ SPIKE';
  else if (hrDelta > 5) hrLabel = '▲ elevated';
  else if (hrDelta > 0) hrLabel = '▲ rising';
  else if (hrDelta < 0) hrLabel = '▼ dropping';
  else hrLabel = '- controlled';

  const hrLine = prev !== r.heartRate
    ? `Heart Rate:     ${prev} → ${r.heartRate} bpm [${hrLabel}]`
    : `Heart Rate:     ${r.heartRate} bpm [${hrLabel}]`;

  const tempLine = `Skin Temp:      ${r.skinTempF}°F / ${r.skinTemp}°C`;

  const lines = [
    'BIO-SCAN ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
    hrLine,
    tempLine,
    `Perspiration:   ${r.perspiration}`,
    `Micro-Expr:     ${r.microExpr}`,
    `Assessment:     ${r.assessment}`,
  ];

  if (r.classification) {
    lines.push('');
    lines.push(`▓▓ ${r.classification.level} ▓▓`);
    lines.push(
      r.classification.status
        ? `${r.classification.bucket} [${r.classification.status}]`
        : r.classification.bucket
    );
  }

  lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  return lines.join('\n');
}

/** Resolve classification metadata for a topic; falls back to default. */
export function getClassification(topic) {
  return (BIOSCAN_TABLE[topic] || BIOSCAN_TABLE.default).classification;
}
